import React, { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { FileTextExtractor } from '@/utils/fileTextExtractor';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Upload,
  FileText,
  Copy,
  Download,
  Loader2,
  Wand2,
  CheckCircle2,
  AlertCircle,
} from 'lucide-react';

const JsonGenerator: React.FC = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Left panel state
  const [resumeText, setResumeText] = useState('');
  const [uploadedFileName, setUploadedFileName] = useState('');
  const [isExtracting, setIsExtracting] = useState(false);
  const [isConverting, setIsConverting] = useState(false);

  // Right panel state
  const [jsonOutput, setJsonOutput] = useState('');
  const [jsonError, setJsonError] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);

  const normalizeResumeSchema = useCallback((value: any) => {
    const toStringValue = (v: unknown) => (v === null || v === undefined ? '' : typeof v === 'string' ? v : String(v));
    const toArray = (v: unknown) => (Array.isArray(v) ? v : []);

    const skillRows = (() => {
      const rows = toArray(value?.SkillRows);
      if (rows.length > 0) {
        return rows.map((item: any) => ({
          Skill1: toStringValue(item?.Skill1),
          Skill2: toStringValue(item?.Skill2),
          Skill3: toStringValue(item?.Skill3),
          Skill4: toStringValue(item?.Skill4),
        }));
      }

      const flatSkills = toArray(value?.Skills)
        .map((item: any) => toStringValue(item?.Skill ?? item))
        .filter((s) => s.trim().length > 0);

      const grouped: Array<{ Skill1: string; Skill2: string; Skill3: string; Skill4: string }> = [];
      for (let i = 0; i < flatSkills.length; i += 4) {
        grouped.push({
          Skill1: flatSkills[i] ?? '',
          Skill2: flatSkills[i + 1] ?? '',
          Skill3: flatSkills[i + 2] ?? '',
          Skill4: flatSkills[i + 3] ?? '',
        });
      }
      return grouped;
    })();

    const mapObjectArray = (arr: unknown, mapper: (item: any) => any) => toArray(arr).map((item) => mapper(item ?? {}));

    return {
      Name: toStringValue(value?.Name),
      Phone: toStringValue(value?.Phone),
      Email: toStringValue(value?.Email),
      City: toStringValue(value?.City),
      State: toStringValue(value?.State),
      LinkedIn: toStringValue(value?.LinkedIn),
      Summary: toStringValue(value?.Summary),
      SkillRows: skillRows,
      CareerHighlights: mapObjectArray(value?.CareerHighlights, (item) => ({ chtitle: toStringValue(item?.chtitle).replace(/:+\s*$/, ''), chdescription: toStringValue(item?.chdescription) })),
      ProfessionalExperience: mapObjectArray(value?.ProfessionalExperience, (item) => ({
        job_title: toStringValue(item?.job_title),
        company: toStringValue(item?.company),
        location: toStringValue(item?.location),
        start_date: toStringValue(item?.start_date),
        end_date: toStringValue(item?.end_date),
        Descriptions: mapObjectArray(item?.Descriptions, (desc) => ({ Description: toStringValue(desc?.Description) })),
      })),
      CertificationsAndLicenses: mapObjectArray(value?.CertificationsAndLicenses, (item) => ({
        Certification_or_License_Name: toStringValue(item?.Certification_or_License_Name),
        Institution_Organization: toStringValue(item?.Institution_Organization),
        Year: toStringValue(item?.Year),
      })),
      Languages: mapObjectArray(value?.Languages, (item) => ({ Language: toStringValue(item?.Language), Proficiency_Level: toStringValue(item?.Proficiency_Level) })),
      AwardsAndHonors: mapObjectArray(value?.AwardsAndHonors, (item) => ({
        Award_Name: toStringValue(item?.Award_Name),
        Institution_Organization: toStringValue(item?.Institution_Organization),
        Year: toStringValue(item?.Year),
      })),
      ProfessionalAffiliations: mapObjectArray(value?.ProfessionalAffiliations, (item) => ({
        Membership_Role: toStringValue(item?.Membership_Role),
        Organization: toStringValue(item?.Organization),
        Year: toStringValue(item?.Year),
      })),
      Publications: mapObjectArray(value?.Publications, (item) => ({
        Title_of_Publication: toStringValue(item?.Title_of_Publication),
        Publication_Name: toStringValue(item?.Publication_Name),
        Date: toStringValue(item?.Date),
      })),
      Projects: mapObjectArray(value?.Projects, (item) => ({
        Project_Name: toStringValue(item?.Project_Name),
        Role: toStringValue(item?.Role),
        Year: toStringValue(item?.Year),
        Project_Description: toStringValue(item?.Project_Description),
      })),
      Presentations: mapObjectArray(value?.Presentations, (item) => ({
        Title_of_Presentation: toStringValue(item?.Title_of_Presentation),
        Event_Name: toStringValue(item?.Event_Name),
        Date: toStringValue(item?.Date),
        Brief_Description: toStringValue(item?.Brief_Description),
      })),
      Portfolio: mapObjectArray(value?.Portfolio, (item) => ({ Portfolio: toStringValue(item?.Portfolio) })),
      TrainingAndDevelopment: mapObjectArray(value?.TrainingAndDevelopment, (item) => ({
        Course_Training_Name: toStringValue(item?.Course_Training_Name),
        Institution_Provider: toStringValue(item?.Institution_Provider),
        Year: toStringValue(item?.Year),
      })),
      MilitaryExperience: mapObjectArray(value?.MilitaryExperience, (item) => ({
        Rank_Position: toStringValue(item?.Rank_Position),
        Branch: toStringValue(item?.Branch),
        Service_Dates: toStringValue(item?.Service_Dates),
        Description_of_duties_or_achievements: toStringValue(item?.Description_of_duties_or_achievements),
      })),
      VolunteerMemberships: mapObjectArray(value?.VolunteerMemberships, (item) => ({
        PositionName: toStringValue(item?.PositionName),
        InstitutionOrganization: toStringValue(item?.InstitutionOrganization),
        Year: toStringValue(item?.Year),
      })),
      Education: mapObjectArray(value?.Education, (item) => ({
        College_Degree_Name: toStringValue(item?.College_Degree_Name),
        Major: toStringValue(item?.Major),
        College_Name: toStringValue(item?.College_Name),
        City: toStringValue(item?.City),
        ST: toStringValue(item?.ST),
        Year_Earned: toStringValue(item?.Year_Earned),
      })),
    };
  }, []);

  // ── File upload ──────────────────────────────────────────────
  const handleFileUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validation = FileTextExtractor.validateFile(file);
    if (!validation.valid) {
      toast.error(validation.error);
      return;
    }

    setIsExtracting(true);
    setUploadedFileName(file.name);

    try {
      const result = await FileTextExtractor.extractText(file);
      if (result.success && result.text) {
        setResumeText(result.text);
        toast.success(`Extracted text from ${file.name}`);
      } else {
        toast.error(result.error || 'Failed to extract text');
      }
    } catch (err) {
      toast.error('Unexpected error reading file');
    } finally {
      setIsExtracting(false);
    }
  }, []);

  // ── Convert to JSON ──────────────────────────────────────────
  const handleConvert = useCallback(async () => {
    if (!resumeText.trim()) {
      toast.error('Please paste or upload resume content first');
      return;
    }

    setIsConverting(true);
    setJsonError('');

    try {
      const { data, error } = await supabase.functions.invoke('parse-resume-to-json', {
        body: { resumeContent: resumeText, documentType: 'resume' },
      });

      if (error) throw error;
      if (!data?.success) throw new Error(data?.error || 'Conversion failed');

      const normalized = normalizeResumeSchema(data.parsedData || {});
      const formatted = JSON.stringify(normalized, null, 2);
      setJsonOutput(formatted);
      toast.success('Resume converted to JSON');
    } catch (err: any) {
      toast.error(err.message || 'Conversion failed');
    } finally {
      setIsConverting(false);
    }
  }, [resumeText, normalizeResumeSchema]);

  // ── JSON validation on edit ──────────────────────────────────
  const handleJsonChange = useCallback((value: string) => {
    setJsonOutput(value);
    if (!value.trim()) {
      setJsonError('');
      return;
    }
    try {
      JSON.parse(value);
      setJsonError('');
    } catch {
      setJsonError('Invalid JSON');
    }
  }, []);

  // ── Copy JSON ────────────────────────────────────────────────
  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(jsonOutput);
      toast.success('Copied to clipboard');
    } catch {
      toast.error('Failed to copy');
    }
  }, [jsonOutput]);

  // ── Download JSON ────────────────────────────────────────────
  const handleDownloadJson = useCallback(() => {
    const blob = new Blob([jsonOutput], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'resume-data.json';
    a.click();
    URL.revokeObjectURL(url);
  }, [jsonOutput]);

  // ── Generate Docmosis resume ─────────────────────────────────
  const handleGenerateDocmosis = useCallback(async () => {
    if (jsonError) {
      toast.error('Fix JSON errors before generating');
      return;
    }

    let parsed: any;
    try {
      parsed = JSON.parse(jsonOutput);
    } catch {
      toast.error('Invalid JSON – cannot generate');
      return;
    }

    setIsGenerating(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://gxatnnzwaggcvzzurgsq.supabase.co';
      const anonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Imd4YXRubnp3YWdnY3Z6enVyZ3NxIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTI2MTQzMzMsImV4cCI6MjA2ODE5MDMzM30.btK3YXr_f0uPm_mwT5q9-8U3d10_QMevDSlC2NSZO_I';

      const response = await fetch(`${supabaseUrl}/functions/v1/generate-resume`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token || anonKey}`,
          'apikey': anonKey,
        },
        body: JSON.stringify({ resumeData: parsed }),
      });

      if (!response.ok) {
        const errText = await response.text();
        throw new Error(errText || `HTTP ${response.status}`);
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${parsed.Name || 'Resume'} Resume 2026.docx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Resume document downloaded!');
    } catch (err: any) {
      toast.error(err.message || 'Generation failed');
    } finally {
      setIsGenerating(false);
    }
  }, [jsonOutput, jsonError]);

  // ── Render ───────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-background p-4 md:p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 max-w-7xl mx-auto">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-2xl font-bold text-foreground">Resume JSON Generator</h1>
        </div>
      </div>

      {/* Two-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-7xl mx-auto">
        {/* ── LEFT PANEL ─────────────────────────────── */}
        <Card className="flex flex-col">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <FileText className="h-5 w-5 text-primary" />
              Resume Input
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-4 flex-1">
            {/* Upload */}
            <div>
              <Label className="mb-1 block">Upload PDF / DOCX</Label>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.docx,.txt"
                className="hidden"
                onChange={handleFileUpload}
              />
              <Button
                variant="outline"
                className="w-full justify-start gap-2"
                onClick={() => fileInputRef.current?.click()}
                disabled={isExtracting}
              >
                {isExtracting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Upload className="h-4 w-4" />
                )}
                {uploadedFileName || 'Choose file…'}
              </Button>
            </div>

            {/* Paste */}
            <div className="flex flex-col flex-1">
              <Label className="mb-1">Or paste resume text</Label>
              <Textarea
                className="flex-1 min-h-[300px] font-mono text-xs"
                placeholder="Paste your resume content here…"
                value={resumeText}
                onChange={(e) => setResumeText(e.target.value)}
              />
            </div>

            {/* Convert button */}
            <Button
              onClick={handleConvert}
              disabled={isConverting || !resumeText.trim()}
              className="w-full gap-2"
            >
              {isConverting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Wand2 className="h-4 w-4" />
              )}
              Convert to JSON
            </Button>
          </CardContent>
        </Card>

        {/* ── RIGHT PANEL ────────────────────────────── */}
        <Card className="flex flex-col">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <CheckCircle2 className="h-5 w-5 text-primary" />
              JSON Output
              {jsonError && (
                <span className="ml-auto flex items-center gap-1 text-destructive text-sm font-normal">
                  <AlertCircle className="h-4 w-4" />
                  {jsonError}
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-4 flex-1">
            <Textarea
              className={`flex-1 min-h-[350px] font-mono text-xs ${
                jsonError ? 'border-destructive' : ''
              }`}
              placeholder="Parsed JSON will appear here…"
              value={jsonOutput}
              onChange={(e) => handleJsonChange(e.target.value)}
            />

            {/* Action buttons */}
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleCopy}
                disabled={!jsonOutput}
                className="gap-1"
              >
                <Copy className="h-4 w-4" /> Copy
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleDownloadJson}
                disabled={!jsonOutput}
                className="gap-1"
              >
                <Download className="h-4 w-4" /> Download JSON
              </Button>
              <Button
                size="sm"
                onClick={handleGenerateDocmosis}
                disabled={!jsonOutput || !!jsonError || isGenerating}
                className="gap-1 ml-auto"
              >
                {isGenerating ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <FileText className="h-4 w-4" />
                )}
                Generate Resume with Docmosis
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default JsonGenerator;
